import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { Paginacion } from "@/components/Paginacion";
import { ResumenTrabajoDialog } from "@/components/ResumenTrabajoDialog";
import { HondaActualizacionDialog } from "@/components/HondaActualizacionDialog";
import { numTrabajo, fmtFecha } from "@/pages/TrabajosActivos";
import { Search, FileDown, FileText, RotateCcw, MessageCircle, RefreshCw } from "lucide-react";

const ESTADOS_WA_ENVIADO = ["enviado", "pendiente", "omitido"];

const fmtFechaCR = (iso) => {
  if (!iso) return "";
  const partes = new Intl.DateTimeFormat("es-CR", {
    timeZone: "America/Costa_Rica", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t) => partes.find((p) => p.type === t)?.value;
  return `${get("day")}/${get("month")} ${get("hour")}:${get("minute")}`;
};

export const descargarPdf = async (url, nombre) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = nombre; a.click();
    URL.revokeObjectURL(u);
  } catch (e) { toast.error(errorMsg(e)); }
};

export default function TrabajosRealizados() {
  const { tienePermiso, isAdmin } = useAuth();
  const hoy = new Date().toLocaleDateString("en-CA");
  const [trabajos, setTrabajos] = useState([]);
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);
  const [waDialog, setWaDialog] = useState(null);
  const [waCargando, setWaCargando] = useState(false);
  const [waEnviando, setWaEnviando] = useState(false);
  const [resumenTrabajoId, setResumenTrabajoId] = useState(null);
  const [hondaLoteId, setHondaLoteId] = useState(null);
  const [cfgFotos, setCfgFotos] = useState(null);
  const [fotosSalidaFaltan, setFotosSalidaFaltan] = useState({});

  useEffect(() => { api.get("/config-fotos").then((r) => setCfgFotos(r.data)).catch(() => {}); }, []);

  // Si el interruptor de fotos de salida está activo, se consulta cuáles trabajos (finalizados
  // después de activarlo) todavía no tienen las fotos obligatorias, para deshabilitar el botón
  // con un tooltip (solo quien tiene permiso trabajos.fotos puede ver el detalle).
  useEffect(() => {
    if (!cfgFotos?.fotos_exigir_salida_para_boleta || !tienePermiso("trabajos.fotos") || !trabajos.length) {
      setFotosSalidaFaltan({}); return;
    }
    const activadoEn = cfgFotos.fotos_exigir_salida_activado_en;
    const candidatos = trabajos.filter((t) => !activadoEn || (t.fecha_finalizacion || "") >= activadoEn);
    let activo = true;
    Promise.all(candidatos.map((t) => api.get(`/trabajos/${t.id}/fotos`)
      .then(({ data }) => [t.id, data?.faltantes?.salida || []]).catch(() => [t.id, []])))
      .then((pares) => {
        if (!activo) return;
        const mapa = {};
        pares.forEach(([id, faltan]) => { if (faltan.length) mapa[id] = faltan; });
        setFotosSalidaFaltan(mapa);
      });
    return () => { activo = false; };
  }, [trabajos, cfgFotos, tienePermiso]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { estado: "finalizado", q: q.trim() || undefined, page, page_size: pageSize };
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      const r = await api.get("/trabajos", { params });
      setTrabajos(r.data.items);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });
    } finally { setCargando(false); }
  }, [q, desde, hasta, page, pageSize]);

  useEffect(() => { setPage(1); }, [q, desde, hasta, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const reactivar = async (t) => {
    if (!window.confirm(`¿Reactivar el trabajo ${numTrabajo(t.numero)}? Volverá a Trabajos Activos.`)) return;
    try {
      await api.post(`/trabajos/${t.id}/reactivar`);
      toast.success("Trabajo reactivado; disponible en Trabajos Activos");
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const actualizarVehiculoDesdeHonda = async (t) => {
    try {
      const { data } = await api.post(`/trabajos/${t.id}/honda-actualizar-vehiculo`);
      setHondaLoteId(data.lote_id);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const visibles = trabajos;

  const abrirWhatsapp = async (t) => {
    setWaCargando(true);
    try {
      const r = await api.get(`/trabajos/${t.id}/whatsapp-aviso`);
      setWaDialog({ trabajo: t, info: r.data });
    } catch (e) {
      if (e?.response?.status === 409) toast.info("El aviso por WhatsApp está desactivado en Datos del taller");
      else toast.error(errorMsg(e));
    } finally { setWaCargando(false); }
  };

  const confirmarEnvioWa = async (reenviar) => {
    setWaEnviando(true);
    try {
      await api.post(`/trabajos/${waDialog.trabajo.id}/whatsapp-aviso`, { reenviar });
      toast.success("Aviso puesto en cola para enviarse al cliente por WhatsApp");
      setWaDialog(null);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setWaEnviando(false); }
  };

  return (
    <div data-testid="trabajos-realizados-page" className="space-y-4">
      <Panel titulo="Trabajos Realizados" testId="trabajos-realizados-panel">
        <div className="flex flex-col md:flex-row gap-3 md:items-end mb-4">
          <div className="relative max-w-md flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="realizados-buscar-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por número, cliente o placa..." className="pl-9" />
          </div>
          <div className="flex gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Desde</label>
              <Input data-testid="realizados-desde-input" type="date" value={desde}
                onChange={(e) => setDesde(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Hasta</label>
              <Input data-testid="realizados-hasta-input" type="date" value={hasta}
                onChange={(e) => setHasta(e.target.value)} className="w-40" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="realizados-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">Número</th>
                <th className="py-2.5 pr-3 font-semibold">Cliente</th>
                <th className="py-2.5 pr-3 font-semibold">Vehículo</th>
                <th className="py-2.5 pr-3 font-semibold">Fecha de finalización</th>
                <th className="py-2.5 pr-3 font-semibold">Mecánico</th>
                <th className="py-2.5 pr-3 font-semibold">Orden Honda</th>
                <th className="py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((t) => (
                <tr key={t.id} data-testid={`realizado-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 pr-3 font-medium text-indigo-700">
                    <button type="button" data-testid={`trabajo-numero-btn-${t.id}`}
                      onClick={(e) => { e.stopPropagation(); setResumenTrabajoId(t.id); }}
                      className="hover:underline underline-offset-2">
                      {numTrabajo(t.numero)}
                    </button>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-800">{t.cliente?.nombre || "—"}</td>
                  <td className="py-2.5 pr-3">{t.vehiculo ? `${t.vehiculo.marca} ${t.vehiculo.modelo} · ${t.vehiculo.placa}` : "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{t.fecha_finalizacion ? fmtFecha(t.fecha_finalizacion) : "—"}</td>
                  <td className="py-2.5 pr-3">{t.empleado?.nombre || "—"}</td>
                  <td className="py-2.5 pr-3" data-testid={`realizado-orden-honda-${t.id}`}>
                    {t.es_cupon ? (t.orden_honda || <span className="text-amber-600">Sin registrar</span>) : "—"}
                  </td>
                  <td className="py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`realizado-boleta-ingreso-btn-${t.id}`} title="Boleta de ingreso (PDF)"
                        onClick={() => descargarPdf(`/trabajos/${t.id}/boleta-pdf`, `boleta_ingreso_${numTrabajo(t.numero)}.pdf`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 hover:bg-teal-100">
                        <FileText size={13} />
                      </button>
                      <button data-testid={`realizado-pdf-btn-${t.id}`}
                        title={fotosSalidaFaltan[t.id] && !isAdmin
                          ? `Faltan fotos de salida: ${fotosSalidaFaltan[t.id].join(", ")}`
                          : "Boleta de salida / Reporte Final (PDF)"}
                        disabled={!!fotosSalidaFaltan[t.id] && !isAdmin}
                        onClick={() => descargarPdf(`/trabajos/${t.id}/reporte-final-pdf`, `reporte_final_${numTrabajo(t.numero)}.pdf`)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed">
                        <FileDown size={13} />
                      </button>
                      {tienePermiso("trabajos.finalizar") && (
                        <button data-testid={`realizado-whatsapp-btn-${t.id}`} title="Avisar al cliente por WhatsApp"
                          onClick={() => abrirWhatsapp(t)} disabled={waCargando}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50">
                          <MessageCircle size={13} />
                        </button>
                      )}
                      {tienePermiso("trabajos.reactivar") && (
                        <button data-testid={`realizado-reactivar-btn-${t.id}`} title="Reactivar (volver a activo)"
                          onClick={() => reactivar(t)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100">
                          <RotateCcw size={13} />
                        </button>
                      )}
                      {tienePermiso("honda.actualizar_vehiculos") && (
                        <button data-testid={`realizado-honda-actualizar-btn-${t.id}`}
                          title="Actualizar placa/chasis desde Honda"
                          onClick={() => actualizarVehiculoDesdeHonda(t)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">
                          <RefreshCw size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {visibles.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400" data-testid="realizados-vacio">
                  No hay trabajos finalizados en el rango seleccionado
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>

      <Dialog open={!!waDialog} onOpenChange={(o) => { if (!o) setWaDialog(null); }}>
        <DialogContent data-testid="whatsapp-aviso-dialog-realizados" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle size={18} className="text-green-600" /> Avisar al cliente por WhatsApp
            </DialogTitle>
            <DialogDescription>{waDialog ? numTrabajo(waDialog.trabajo.numero) : ""}</DialogDescription>
          </DialogHeader>
          {waDialog && (
            <>
              {waDialog.trabajo.aviso_whatsapp_omitido && !waDialog.info.ultimo_envio && (
                <p data-testid="whatsapp-aviso-omitido-previo" className="text-xs text-slate-400">
                  Al finalizar, {waDialog.trabajo.aviso_whatsapp_omitido.usuario} eligió no avisar
                  ({fmtFechaCR(waDialog.trabajo.aviso_whatsapp_omitido.fecha)}).
                </p>
              )}
              {!waDialog.info.telefono_valido ? (
              <>
                <p data-testid="whatsapp-aviso-sin-telefono-realizados" className="text-sm text-amber-700">
                  El cliente no tiene un teléfono válido registrado; no se puede enviar el aviso.
                </p>
                <div className="flex justify-end">
                  <Button data-testid="whatsapp-aviso-cerrar-realizados" onClick={() => setWaDialog(null)}>Entendido</Button>
                </div>
              </>
            ) : waDialog.info.ultimo_envio && ESTADOS_WA_ENVIADO.includes(waDialog.info.ultimo_envio.estado) ? (
              <>
                <p data-testid="whatsapp-aviso-ya-enviado" className="text-sm text-slate-700">
                  Ya se envió el {fmtFecha(waDialog.info.ultimo_envio.enviado_at || waDialog.info.ultimo_envio.creado_en)}.
                  ¿Enviar de nuevo?
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" data-testid="whatsapp-aviso-cancelar" onClick={() => setWaDialog(null)}>Cancelar</Button>
                  <Button data-testid="whatsapp-aviso-reenviar" disabled={waEnviando} onClick={() => confirmarEnvioWa(true)}
                    className="bg-green-600 hover:bg-green-700 text-white">
                    {waEnviando ? "Enviando..." : "Enviar de nuevo"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-500">Se enviará al número <b>{waDialog.info.telefono}</b>:</p>
                <p data-testid="whatsapp-aviso-preview-realizados" className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 text-slate-700">
                  {waDialog.info.texto_preview}
                </p>
                {waDialog.info.ultimo_envio && (
                  <p data-testid="whatsapp-aviso-estado-anterior" className="text-xs text-amber-600">
                    Último intento: {waDialog.info.ultimo_envio.estado}
                    {waDialog.info.ultimo_envio.error ? ` (${waDialog.info.ultimo_envio.error})` : ""}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" data-testid="whatsapp-aviso-cancelar" onClick={() => setWaDialog(null)}>Cancelar</Button>
                  <Button data-testid="whatsapp-aviso-confirmar" disabled={waEnviando} onClick={() => confirmarEnvioWa(false)}
                    className="bg-green-600 hover:bg-green-700 text-white">
                    {waEnviando ? "Enviando..." : "Enviar aviso"}
                  </Button>
                </div>
              </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ResumenTrabajoDialog trabajoId={resumenTrabajoId} mostrarRealizados={true}
        onClose={() => setResumenTrabajoId(null)} />

      {tienePermiso("honda.actualizar_vehiculos") && (
        <HondaActualizacionDialog loteId={hondaLoteId} onClose={() => setHondaLoteId(null)} onAplicado={cargar} />
      )}
    </div>
  );
}
